import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable, of } from 'rxjs'
import { map } from 'rxjs/operators'
import { FeeModalities, FeeTypes } from '@tokeet/cost-resolver'
import { Rental, RentalSecurityDepositConfig } from '@tokeet-frontend/tv3-platform'

export interface RentalSecurityDepositApiModel {
  amount: number | string
  type: FeeTypes
  modality: FeeModalities
  status: number | boolean
}

export type RentalSecurityDepositUpdatePayload = Pick<
  RentalSecurityDepositConfig,
  'amount' | 'type' | 'modality' | 'status'
>

@Injectable({
  providedIn: 'root',
})
export class RentalSecurityDepositService {
  constructor(private http: HttpClient) {}

  loadConfig(rental: Rental): Observable<RentalSecurityDepositConfig | null> {
    const config = (rental as any)?.securityDepositConfig ?? (rental as any)?.security_deposit_config

    if (!config) {
      return of(null)
    }

    return of(this.normalize(config as RentalSecurityDepositApiModel))
  }

  saveConfig(rentalId: string, payload: RentalSecurityDepositUpdatePayload): Observable<RentalSecurityDepositConfig> {
    const url = `@api/rental/update/${encodeURIComponent(rentalId)}`
    const body = {
      security_deposit_config: this.serialize(payload),
    }

    return this.http.put<{ security_deposit_config?: RentalSecurityDepositApiModel }>(url, body).pipe(
      map((response) => {
        const config =
          response?.security_deposit_config ??
          ({
            ...payload,
            status: payload.status ? 1 : 0,
          } as RentalSecurityDepositApiModel)

        return this.normalize(config)
      })
    )
  }

  private serialize(
    payload: RentalSecurityDepositUpdatePayload
  ): Omit<RentalSecurityDepositUpdatePayload, 'status'> & { status: number } {
    const { status, ...rest } = payload
    return {
      ...rest,
      status: status ? 1 : 0,
    }
  }

  private normalize(config: RentalSecurityDepositApiModel): RentalSecurityDepositConfig {
    return {
      amount: Number(config.amount) || 0,
      type: config.type,
      modality: config.modality,
      status: Number(config.status) === 1 || config.status === true,
    }
  }
}
